import React from 'react';

import {
    CreateBasePlanInput,
    CreateBasePlanLaunchDatesInput,
    CreateBasePlanOutput,
    ModifyBasePlanLaunchDatesInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBasePlanLaunchDate } from 'src/types/IBasePlanLaunchDate';
import { IToast } from 'src/types/IToaster';
import { getDateAndTimestamp } from 'src/util/dateTimeUtil';

export interface IBasePlanLaunchDateReviewProps {
    basePlanLaunchDate: IBasePlanLaunchDate;
    onPrevious: () => void;
    onCancel: () => void;
    isEditMode: boolean;
}

export const BasePlanLaunchDateReview = ({
    basePlanLaunchDate,
    onPrevious,
    onCancel,
    isEditMode
}: IBasePlanLaunchDateReviewProps) => {
    const { metadata, setToasts, setTopAlertMsg, userInfo } = React.useContext(AppContext);
    const history = useHistory();

    const handleAdd = () => {
        if (basePlanLaunchDate.basePlanId) {
            addBasePlanLaunchDate();
        } else {
            addBasePlanLaunchWithNewBasePlan();
        }
    };

    const handleApiSuccess = () => {
        const toast: IToast = isEditMode
            ? {
                  id: 'edit-base-plan-launch-date-toast',
                  timeout: DEFAULT_TOASTER_TIMEOUT,
                  message: 'Base plan launch date changes saved'
              }
            : {
                  id: 'add-base-plan-launch-date-toast',
                  timeout: DEFAULT_TOASTER_TIMEOUT,
                  message: 'New launch date added'
              };
        setToasts([toast]);
        history.push(ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX);
    };

    const handleApiFail = (apiName: string, message: string) => {
        // eslint-disable-next-line no-console
        console.error(`${apiName} unexpected error`, message);
        setTopAlertMsg(message);
    };

    const addBasePlanLaunchWithNewBasePlan = () => {
        const createBasePlanPromise = contractConfigurationClient
            .createBasePlan(
                CreateBasePlanInput.clone({
                    basePlanName: basePlanLaunchDate.basePlanName,
                    createdBy: userInfo.username
                })
            )
            .toPromise();

        createBasePlanPromise
            .then((response: CreateBasePlanOutput) => {
                return contractConfigurationClient
                    .createBasePlanLaunchDates(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        CreateBasePlanLaunchDatesInput.clone(mapBasePlanLaunchDateToAddInput(response.basePlanId!))
                    )
                    .toPromise();
            })
            .then(() => {
                handleApiSuccess();
            })
            .catch((error: UnknownCoralError) => {
                handleApiFail('createBasePlan or createEntity', error.receivedException?.message || '');
            });
    };

    const addBasePlanLaunchDate = () => {
        contractConfigurationClient
            .createBasePlanLaunchDates(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                CreateBasePlanLaunchDatesInput.clone(mapBasePlanLaunchDateToAddInput(basePlanLaunchDate.basePlanId!))
            )
            .subscribe(
                () => {
                    handleApiSuccess();
                },
                (error: UnknownCoralError) => {
                    handleApiFail('createEntity', error.receivedException?.message || '');
                }
            );
    };

    const editBasePlanLaunchDate = () => {
        contractConfigurationClient
            .modifyBasePlanLaunchDates(ModifyBasePlanLaunchDatesInput.clone(mapBasePlanLaunchDateToEditInput()))
            .subscribe(
                () => {
                    handleApiSuccess();
                },
                (error: UnknownCoralError) => {
                    handleApiFail('modifyBasePlanLaunchDates', error.receivedException?.message || '');
                }
            );
    };

    const mapBasePlanLaunchDateToAddInput = (basePlanId: number) => {
        return {
            basePlanId: basePlanId,
            createdBy: userInfo.username,
            endDate: basePlanLaunchDate.endDate ? getDateAndTimestamp(basePlanLaunchDate.endDate)[0] : undefined,
            startDate: basePlanLaunchDate.startDate ? getDateAndTimestamp(basePlanLaunchDate.startDate)[0] : undefined,
            territories: basePlanLaunchDate.territoryIds
        } as CreateBasePlanLaunchDatesInput;
    };

    const mapBasePlanLaunchDateToEditInput = () => {
        return {
            basePlanLaunchDateId: basePlanLaunchDate.basePlanLaunchDateId,
            changedBy: userInfo.username,
            updatedEndDate: basePlanLaunchDate.endDate ? getDateAndTimestamp(basePlanLaunchDate.endDate)[0] : undefined,
            updatedStartDate: basePlanLaunchDate.startDate
                ? getDateAndTimestamp(basePlanLaunchDate.startDate)[0]
                : undefined,
            updatedTerritory: basePlanLaunchDate.territoryIds[0]
        } as ModifyBasePlanLaunchDatesInput;
    };

    return (
        <>
            <Heading level={1}>{isEditMode ? 'Review and save' : 'Review and add'}</Heading>
            <Column spacingInset="450 none 500 none" spacing="450">
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Base plan ID
                    </Text>
                    <Text>{basePlanLaunchDate.basePlanName}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Territory
                    </Text>
                    {basePlanLaunchDate.territoryIds.map((territoryId) => (
                        <TerritoryFlag
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            territoryCode={metadata.territoryMapping[territoryId].territoryCode!}
                            showTerritoryCode={false}
                            showTerritoryName={true}
                            key={territoryId}
                        />
                    ))}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Start date
                    </Text>
                    <Text>{basePlanLaunchDate.startDate}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        End date
                    </Text>
                    <Text>{basePlanLaunchDate.endDate || '--'}</Text>
                </Column>
            </Column>
            <Row alignmentHorizontal="end">
                <Button type="link" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="tertiary" onClick={onPrevious}>
                    Previous
                </Button>
                <Button type="primary" onClick={isEditMode ? editBasePlanLaunchDate : handleAdd}>
                    {isEditMode ? 'Save changes' : 'Add launch date'}
                </Button>
            </Row>
        </>
    );
};
